import getConfig from "next/config";
import Cookie from "js-cookie";

import { paths, components } from "./api-types";
import { isServer } from "./pages/_app";
import { SIMULATED_COUNTRY_COOKIE_NAME, SIMULATED_COUNTRY_HEADER_NAME } from "./utils/constants";

export class ApiClient {
  public ssrCookie?: string = undefined;
  public ssrCookies?: { [key: string]: string };

  get<ResponseType>(
    url: ApiUrl,
    pathParams: UrlParams = {},
    queryParams: UrlParams = {},
  ): Promise<RestCallResult<ResponseType>> {
    const fullUrl = makeFullUrl(url, pathParams || {}, queryParams || {});
    const headers = this.getAuthHeaders();
    return callFetch<ResponseType>(
      fetch(fullUrl, { credentials: "include", method: "GET", headers }),
    );
  }

  post<ResponseType>(
    url: ApiUrl,
    payload: unknown,
    pathParams: UrlParams = {},
    queryParams: UrlParams = {},
  ): Promise<RestCallResult<ResponseType>> {
    return this.withBodyRequest("POST", url, payload, pathParams || {}, queryParams || {});
  }

  patch<ResponseType>(
    url: ApiUrl,
    payload: unknown,
    pathParams: UrlParams = {},
    queryParams: UrlParams = {},
  ): Promise<RestCallResult<ResponseType>> {
    return this.withBodyRequest("PATCH", url, payload, pathParams || {}, queryParams || {});
  }

  public getSimulatedCountry(): string {
    const jsValue = Cookie.get(SIMULATED_COUNTRY_COOKIE_NAME);
    if (jsValue) {
      return jsValue;
    }
    const ssrValue = (this.ssrCookies ?? {})[SIMULATED_COUNTRY_COOKIE_NAME];
    if (ssrValue) {
      return ssrValue;
    }
    return "";
  }

  private getAuthHeaders(): { [key: string]: string } {
    const result: { [key: string]: string } = {};
    if (isServer) {
      result["Cookie"] = this.ssrCookie ?? "";
    }

    const country = this.getSimulatedCountry();
    if (country) {
      result[SIMULATED_COUNTRY_HEADER_NAME] = country;
    }

    return result;
  }

  private withBodyRequest<ResponseType>(
    method: "POST" | "PATCH",
    url: ApiUrl,
    payload: unknown,
    pathParams: UrlParams = {},
    queryParams: UrlParams = {},
  ): Promise<RestCallResult<ResponseType>> {
    const fullUrl = makeFullUrl(url, pathParams || {}, queryParams || {});
    const headers = { "Content-Type": "application/json", ...this.getAuthHeaders() };
    return callFetch<ResponseType>(
      fetch(fullUrl, {
        credentials: "include",
        method,
        body: JSON.stringify(payload),
        headers,
      }),
    );
  }
}

export type ApiUrl = keyof paths;
export type Genome = components["schemas"]["Genome"];
export type PaginatedGenomesQuery = components["schemas"]["PaginatedGenomesQuery"];
export type GenomeInList = components["schemas"]["GenomeInList"];
export type TaxonWithParents = components["schemas"]["TaxonWithParents"];
export type AnnotationSet = components["schemas"]["AnnotationSet"];
export type Annotation = components["schemas"]["Annotation"];
export type AnnotationsResult = components["schemas"]["AnnotationsResult"];
export type AnnotationSequence = components["schemas"]["AnnotationSequence"];
export type AmrSource = components["schemas"]["Annotation"]["amr_sources"][number];
export type GenomeStarred = components["schemas"]["GenomeStarred"];
export type DownloadLink = components["schemas"]["DownloadLink"];
export type AssemblyQCStatistics = components["schemas"]["AssemblyQCStatistics"];
export type ContigStatistics = components["schemas"]["ContigStatistics"];
export type ATCCGenomeMetadata = components["schemas"]["ATCCGenomeMetadata"];
export type CollectionName = components["schemas"]["Genome"]["collection_name"];
export type GenomicVariant = components["schemas"]["GenomicVariant"];
export type PaginationMetadata = components["schemas"]["PaginationMetadata"];
export type AssemblyDerivedMetadata = components["schemas"]["AssemblyDerivedMetadata"];
export type GenomePublicationChange = components["schemas"]["GenomePublicationChange"];
export type GenomesOrderBy = Exclude<
  components["schemas"]["PaginatedGenomesQuery"]["order_by"],
  undefined
>;
export type AtccQcStatistics = components["schemas"]["AtccQcStatistics"];
export type AtccSequencingQualityControl = components["schemas"]["AtccSequencingQualityControl"];
export type AnnotationsSummary = components["schemas"]["AtccAnnotationsSummary"];
export type StripeSessionResponse = components["schemas"]["StripeSessionResponse"];
export type UserData = components["schemas"]["UserData"];
export type CreateSubscriptionResponse = components["schemas"]["CreateSubscriptionResponse"];
export type DiscrepancyReportRun = components["schemas"]["DiscrepancyReportRun"];
export type GenomesBulkDownloadPayload = components["schemas"]["GenomesBulkDownloadRequest"];
export type GenomesBulkDownloadQueryResult =
  components["schemas"]["GenomesBulkDownloadQueryResult"];
export type GenomeDataset = components["schemas"]["GenomeDataset"];
export type CatalogTreeQueryResult = components["schemas"]["CatalogTreeQueryResult"];
export type CarouselCustomHtml = components["schemas"]["CustomHtml"];
export type DiscrepancyReportSample = components["schemas"]["DiscrepancyReportSample"];

const { publicRuntimeConfig } = getConfig();

type UrlParams = Record<string, string | number | boolean | null | string[]>;

export type RestCallResult<ResponseType> =
  | {
      error: string;
      statusCode: number;
      data: undefined;
      headers: undefined;
    }
  | {
      error: undefined;
      statusCode: number;
      data: ResponseType;
      headers: Response["headers"];
    };

function makeFullUrl(url: ApiUrl, pathParams: UrlParams, queryParams: UrlParams): string {
  let result: string = url;
  for (const key of Object.keys(pathParams)) {
    const val = encodeURIComponent(`${pathParams[key]}`);
    result = result.replace(`{${key}}`, val);
  }

  const query = new URLSearchParams();
  const queryKeys = Object.keys(queryParams);
  if (queryKeys.length > 0) {
    for (const key of queryKeys) {
      const val = queryParams[key];
      if (Array.isArray(val)) {
        for (const v of val) {
          query.append(key, `${v}`);
        }
      } else {
        query.set(key, `${val}`);
      }
    }
    result += "?" + query.toString();
  }

  return publicRuntimeConfig.BASE_URL + result;
}

async function callFetch<ResponseType>(
  fetchPromise: Promise<Response>,
): Promise<RestCallResult<ResponseType>> {
  let error: string;
  let statusCode: number;
  try {
    const res = await fetchPromise;
    if (res.ok) {
      return {
        error: undefined,
        statusCode: res.status,
        data: res.status === 204 ? ({} as ResponseType) : ((await res.json()) as ResponseType),
        headers: res.headers,
      };
    } else {
      statusCode = res.status;
      error = `${res.status}: ${res.statusText || "Unknown Error"}`;
    }
  } catch (err) {
    statusCode = -1;
    error = "Network Error";
  }
  return {
    error,
    statusCode: statusCode,
    data: undefined,
    headers: undefined,
  };
}
